import React, { useState, useMemo } from 'react';

interface FileItem {
    name: string;
    content: string;
    fullId?: string;
    path?: string;
    fileName?: string;
    id?: string;
}

interface EnhancedFileSelectorProps {
    files: FileItem[];
    selectedFiles: string[];
    onSelectionChange: (files: string[]) => void;
}

// File categorization logic
const categorizeFile = (fileName: string): 'frontend' | 'backend' | 'config' | 'other' => {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    const path = fileName.toLowerCase();

    // Config files
    if (['json', 'toml', 'yaml', 'yml', 'config', 'env'].includes(extension) ||
        fileName.includes('config') ||
        fileName.includes('.eslintrc') ||
        fileName.includes('.prettierrc') ||
        fileName.endsWith('package.json') ||
        fileName.endsWith('tsconfig.json') ||
        fileName.endsWith('dfx.json')) {
        return 'config';
    }

    // Backend files (Motoko)
    if (extension === 'mo' ||
        extension === '.did.d.ts' ||
        path.includes('/backend/') ||
        path.includes('/src/backend/')) {
        return 'backend';
    }

    // Frontend files (web files)
    if (['tsx', 'ts', 'jsx', 'js', 'css', 'scss', 'html'].includes(extension) ||
        path.includes('/frontend/') ||
        path.includes('/src/frontend/')) {
        return 'frontend';
    }

    // Everything else (docs, images, misc files)
    return 'other';
};

const EnhancedFileSelector: React.FC<EnhancedFileSelectorProps> = ({
                                                                       files,
                                                                       selectedFiles,
                                                                       onSelectionChange
                                                                   }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [activeFilters, setActiveFilters] = useState<Set<string>>(new Set());

    const getFileIcon = (fileName: string) => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        switch (extension) {
            case 'mo': return '🟣';
            case 'tsx':
            case 'ts': return '🔵';
            case 'jsx':
            case 'js': return '🟡';
            case 'css': return '🎨';
            case 'json': return '📋';
            case 'md': return '📝';
            default: return '📄';
        }
    };

    // Order files by extension
    const orderedFiles = useMemo(() => {
        return [...files].sort((a, b) => {
            const extA = a.name.split('.').pop()?.toLowerCase() || '';
            const extB = b.name.split('.').pop()?.toLowerCase() || '';

            const extensionOrder: { [key: string]: number } = {
                'mo': 1, 'tsx': 2, 'ts': 3, 'js': 4, 'jsx': 5,
                'css': 6, 'scss': 7, 'json': 8, 'md': 9, 'txt': 10,
            };

            const orderA = extensionOrder[extA] || 999;
            const orderB = extensionOrder[extB] || 999;

            if (orderA !== orderB) return orderA - orderB;
            return a.name.localeCompare(b.name);
        });
    }, [files]);

    // Filter logic
    const filteredFiles = useMemo(() => {
        let result = orderedFiles;

        // Apply search filter
        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase();
            result = result.filter(file =>
                file.name.toLowerCase().includes(query)
            );
        }

        // Apply category filters (stackable)
        if (activeFilters.size > 0) {
            result = result.filter(file => {
                const category = categorizeFile(file.name);
                return activeFilters.has(category);
            });
        }

        return result;
    }, [orderedFiles, searchQuery, activeFilters]);

    const toggleFilter = (filter: string) => {
        setActiveFilters(prev => {
            const newFilters = new Set(prev);
            if (newFilters.has(filter)) {
                newFilters.delete(filter);
            } else {
                newFilters.add(filter);
            }
            return newFilters;
        });
    };

    const toggleFile = (fileName: string) => {
        const newSelection = selectedFiles.includes(fileName)
            ? selectedFiles.filter(f => f !== fileName)
            : [...selectedFiles, fileName];
        onSelectionChange(newSelection);
    };

    const selectAll = () => {
        onSelectionChange(filteredFiles.map(f => f.name));
    };

    const clearAll = () => {
        onSelectionChange([]);
    };

    const allFilePaths = files.filter(file => file.name && file.name.trim().length > 0).map(file => file.name);
    const selectedCount = selectedFiles.length;
    const totalCount = allFilePaths.length;
    const filteredCount = filteredFiles.length;

    return (
        <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: '#fdf6e3',
            border: '1px solid #8B4513',
            borderTop: 'none',
            maxHeight: '400px',
            overflowY: 'auto',
            zIndex: 1000,
            boxShadow: '0 8px 16px rgba(139, 69, 19, 0.2)',
            borderRadius: '0 0 8px 8px'
        }}>
            {/* Header with selection count */}
            <div style={{
                padding: '16px',
                backgroundColor: '#f3e5d8',
                borderBottom: '1px solid rgba(139, 69, 19, 0.2)',
                position: 'sticky',
                top: 0,
                zIndex: 1
            }}>
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '12px'
                }}>
                    <span style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#5C4033'
                    }}>
                        📁 Select Files for AI Context
                    </span>
                    <span style={{
                        fontSize: '12px',
                        color: '#8B4513',
                        backgroundColor: 'rgba(139, 69, 19, 0.1)',
                        padding: '4px 8px',
                        borderRadius: '4px'
                    }}>
                        {selectedCount} of {totalCount} selected
                    </span>
                </div>

                {/* Search bar */}
                <div style={{ marginBottom: '12px' }}>
                    <input
                        type="text"
                        placeholder="🔍 Search files..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '8px 12px',
                            border: '1px solid #8B4513',
                            borderRadius: '6px',
                            fontSize: '13px',
                            backgroundColor: '#fff',
                            color: '#5C4033',
                            outline: 'none',
                            boxSizing: 'border-box'
                        }}
                    />
                    {searchQuery && (
                        <div style={{
                            marginTop: '6px',
                            fontSize: '11px',
                            color: '#8B4513'
                        }}>
                            Showing {filteredCount} of {totalCount} files
                        </div>
                    )}
                </div>

                {/* Filter buttons (stackable) */}
                <div style={{
                    display: 'flex',
                    gap: '6px',
                    marginBottom: '12px',
                    flexWrap: 'wrap'
                }}>
                    <button
                        onClick={() => toggleFilter('frontend')}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: activeFilters.has('frontend') ? '#2563eb' : 'transparent',
                            color: activeFilters.has('frontend') ? '#fff' : '#2563eb',
                            border: '1px solid #2563eb',
                            borderRadius: '6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        🔵 Frontend
                    </button>
                    <button
                        onClick={() => toggleFilter('backend')}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: activeFilters.has('backend') ? '#7c3aed' : 'transparent',
                            color: activeFilters.has('backend') ? '#fff' : '#7c3aed',
                            border: '1px solid #7c3aed',
                            borderRadius: '6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        🟣 Backend
                    </button>
                    <button
                        onClick={() => toggleFilter('config')}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: activeFilters.has('config') ? '#059669' : 'transparent',
                            color: activeFilters.has('config') ? '#fff' : '#059669',
                            border: '1px solid #059669',
                            borderRadius: '6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        📋 Config
                    </button>
                    <button
                        onClick={() => toggleFilter('other')}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: activeFilters.has('other') ? '#f59e0b' : 'transparent',
                            color: activeFilters.has('other') ? '#fff' : '#f59e0b',
                            border: '1px solid #f59e0b',
                            borderRadius: '6px',
                            fontSize: '11px',
                            cursor: 'pointer',
                            fontWeight: '500',
                            transition: 'all 0.2s ease'
                        }}
                    >
                        📦 Other
                    </button>
                    {activeFilters.size > 0 && (
                        <button
                            onClick={() => setActiveFilters(new Set())}
                            style={{
                                padding: '6px 12px',
                                backgroundColor: '#dc2626',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                fontSize: '11px',
                                cursor: 'pointer',
                                fontWeight: '500'
                            }}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                        onClick={selectAll}
                        disabled={filteredCount === 0 || selectedCount === filteredCount}
                        style={{
                            flex: 1,
                            padding: '8px 12px',
                            backgroundColor: (filteredCount === 0 || selectedCount === filteredCount) ? '#9B5523' : '#4B5320',
                            color: '#FFF5EE',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: (filteredCount === 0 || selectedCount === filteredCount) ? 'not-allowed' : 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        ✓ Select All
                    </button>

                    <button
                        onClick={clearAll}
                        disabled={selectedCount === 0}
                        style={{
                            flex: 1,
                            padding: '8px 12px',
                            backgroundColor: selectedCount === 0 ? '#9B5523' : '#8B4513',
                            color: '#FFF5EE',
                            border: 'none',
                            borderRadius: '6px',
                            fontSize: '12px',
                            cursor: selectedCount === 0 ? 'not-allowed' : 'pointer',
                            fontWeight: '500'
                        }}
                    >
                        ✗ Clear All
                    </button>
                </div>
            </div>

            {/* File list */}
            <div style={{ padding: '12px' }}>
                {filteredFiles.length === 0 ? (
                    <div style={{
                        padding: '40px 20px',
                        textAlign: 'center',
                        color: '#8B4513',
                        fontSize: '14px'
                    }}>
                        {searchQuery || activeFilters.size > 0
                            ? 'No files match your search or filters'
                            : 'No files available'
                        }
                    </div>
                ) : (
                    filteredFiles.map(file => {
                        const isSelected = selectedFiles.includes(file.name);
                        const extension = file.name.split('.').pop()?.toLowerCase() || '';
                        const fileIcon = getFileIcon(file.name);
                        const fileSize = file.content?.length || 0;
                        const fileSizeKB = (fileSize / 1024).toFixed(1);
                        const lines = file.content?.split('\n').length || 0;
                        const category = categorizeFile(file.name);

                        const categoryColors = {
                            frontend: '#2563eb',
                            backend: '#7c3aed',
                            config: '#059669',
                            other: '#f59e0b'
                        };

                        return (
                            <div
                                key={file.name}
                                onClick={() => toggleFile(file.name)}
                                style={{
                                    padding: '12px 16px',
                                    cursor: 'pointer',
                                    backgroundColor: isSelected ? '#e8d5c4' : 'transparent',
                                    borderRadius: '6px',
                                    marginBottom: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    transition: 'all 0.2s ease',
                                    border: isSelected ? '1px solid #5C4033' : '1px solid transparent'
                                }}
                                onMouseEnter={(e) => {
                                    if (!isSelected) {
                                        e.currentTarget.style.backgroundColor = '#f0e8dc';
                                    }
                                }}
                                onMouseLeave={(e) => {
                                    if (!isSelected) {
                                        e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                }}
                            >
                                <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {}}
                                    style={{ margin: 0, accentColor: '#5C4033' }}
                                />
                                <span style={{ fontSize: '16px' }}>{fileIcon}</span>
                                <div style={{ flex: 1 }}>
                                    <div style={{
                                        fontSize: '14px',
                                        color: '#5C4033',
                                        fontWeight: '500',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                        flexWrap: 'wrap'
                                    }}>
                                        {file.name.split('/').pop()}
                                        <span style={{
                                            fontSize: '10px',
                                            color: '#8B4513',
                                            backgroundColor: 'rgba(139, 69, 19, 0.1)',
                                            padding: '2px 6px',
                                            borderRadius: '3px'
                                        }}>
                                            .{extension}
                                        </span>
                                        <span style={{
                                            fontSize: '10px',
                                            color: '#fff',
                                            backgroundColor: categoryColors[category],
                                            padding: '2px 6px',
                                            borderRadius: '3px',
                                            fontWeight: '600'
                                        }}>
                                            {category}
                                        </span>
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#8B4513',
                                        marginTop: '4px',
                                        display: 'flex',
                                        gap: '12px'
                                    }}>
                                        <span>{lines} lines</span>
                                        <span>{fileSizeKB} KB</span>
                                        <span style={{
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap',
                                            maxWidth: '200px'
                                        }}>
                                            {file.name.includes('/')
                                                ? file.name.substring(0, file.name.lastIndexOf('/'))
                                                : 'Root'
                                            }
                                        </span>
                                    </div>
                                </div>
                                {isSelected && (
                                    <span style={{
                                        fontSize: '12px',
                                        color: '#4B5320',
                                        fontWeight: '600',
                                        backgroundColor: 'rgba(75, 83, 32, 0.15)',
                                        padding: '4px 8px',
                                        borderRadius: '4px'
                                    }}>
                                        ✓
                                    </span>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default EnhancedFileSelector;